import * as Yup from 'yup';
import {
  format as formatDate,
  isValid as isValidDate,
  parse as parseDate,
} from 'date-fns';
import debounce from 'debounce-promise';

import { CollapseSection } from '@/components/ats';
import {
  fetchAccountUsersSearch,
  accountUserFullName,
} from '@/api/useAccountUsers';

import type { DarwinboxFormField } from './api';

// The tenant's configured Darwinbox date format (field.format, e.g.
// "DD-MM-YYYY") as a date-fns token, for both parsing a prefilled value and
// formatting what gets submitted. Keep in sync with the BE's
// GlobalIntegration::DARWINBOX_DATE_FORMATS — Darwinbox rejects a date that
// doesn't match the tenant's own "Default Date Format" setting.
const DEFAULT_DARWINBOX_DATE_FORMAT = 'DD-MM-YYYY';
const DATE_FORMAT_TOKENS: Record<string, string> = {
  'DD-MM-YYYY': 'dd-MM-yyyy',
  'MM-DD-YYYY': 'MM-dd-yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
};

const dateFnsTokenFor = (format?: string | null): string =>
  DATE_FORMAT_TOKENS[format || ''] ||
  DATE_FORMAT_TOKENS[DEFAULT_DARWINBOX_DATE_FORMAT];

// react-select drops empty-string option values; hold a sentinel, decode at submit.
const EMPTY_CODE_PREFIX = '__darwinbox_empty_code__::';

// Match the lighter select placeholder weight on inputs.
const INPUT_PLACEHOLDER_STYLES = { fontWeight: 'normal' };

const sectionWrapperProps = {
  title: 'EMPLOYEE DETAILS',
  isHeaderClickable: true,
  defaultIsOpen: true,
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: { color: 'ats-primary' },
    iconStyles: { width: '24px', height: '24px' },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em',
    backgroundColor: 'white',
  },
};

// Manager field ships no options; offer account users and submit the selected email.
const isManagerField = (field: DarwinboxFormField) =>
  /manager\s*email/i.test(field.id) || /manager\s*email/i.test(field.label);

const loadAccountUserOptions = async (input: string) => {
  const { users } = await fetchAccountUsersSearch(input || '');
  return users
    .filter((user) => user.email)
    .map((user) => ({
      label: `${accountUserFullName(user)} — ${user.email}`,
      value: user.email,
    }));
};

// AsyncSelectField's debounce is a no-op; debounce at the source (one request per pause).
const debouncedLoadAccountUserOptions = debounce(loadAccountUserOptions, 300);

// Render 2-option Yes/No enums as a radio group.
const isYesNoOptions = (options: DarwinboxFormField['options']): boolean => {
  if (!Array.isArray(options) || options.length !== 2) return false;
  const labels = options.map((opt) => {
    const text = typeof opt === 'string' ? opt : opt.name;
    return (text || '').trim().toLowerCase();
  });
  return labels.includes('yes') && labels.includes('no');
};

const buildFieldConfig = (field: DarwinboxFormField) => {
  const base = {
    name: field.id,
    label: field.label,
    isRequired: field.required,
    placeholder: field.placeholder || `Enter ${field.label}`,
    optionalValidation: true,
    // A locked field's value always comes from a Kula mapping or a default
    // (enforced when the admin sets it up) — Darwinbox ignores anything
    // typed here, so don't let the recruiter think otherwise.
    disabled: Boolean(field.locked),
  } as Record<string, any>;

  // Real BE options should win over the account-user picker.
  if (isManagerField(field) && !field.options?.length) {
    const validation = field.required
      ? Yup.string().required(`${field.label} is required`)
      : Yup.string();
    return {
      ...base,
      type: 'asyncSelect',
      placeholder: 'Search team members...',
      valueKey: 'value',
      labelKey: 'label',
      isClearable: true,
      loadDefaultOptions: true,
      customLoadOptions: (input: string) =>
        debouncedLoadAccountUserOptions(input),
      // Seed the prefilled value so the chip renders before any search.
      ...(field.value
        ? {
            defaultOptions: [
              { label: String(field.value), value: String(field.value) },
            ],
          }
        : {}),
      validation,
    };
  }

  switch (field.type) {
    case 'string': {
      // Email fields get format validation (manager picker handled above).
      const isEmailField =
        /email/i.test(field.id) || /email/i.test(field.label);
      const stringSchema = isEmailField
        ? Yup.string().email('Enter a valid email address')
        : Yup.string();
      const validation = field.required
        ? stringSchema.required(`${field.label} is required`)
        : stringSchema;
      return {
        ...base,
        type: 'input',
        placeholderStyles: INPUT_PLACEHOLDER_STYLES,
        validation,
      };
    }
    case 'date': {
      const validation = field.required
        ? Yup.mixed().required(`${field.label} is required`)
        : Yup.mixed();
      const outputToken = dateFnsTokenFor(field.format);
      return {
        ...base,
        type: 'date',
        // Format the picked Date; prefilled Dates left untouched are normalized
        // at submit instead (see normalizeDarwinboxValuesForSubmit) — this raw
        // BE string rides along on the config so EmployeeFormDrawer can read it
        // back via getDarwinboxDateFormat without re-fetching the schema.
        rawDateFormat: field.format,
        dateFormat: (date: Date) => (date ? formatDate(date, outputToken) : ''),
        validation,
      };
    }
    case 'enum': {
      // No options (Darwinbox unreachable / unbackfilled) — plain input.
      if (!field.options?.length) {
        const validation = field.required
          ? Yup.string().required(`${field.label} is required`)
          : Yup.string();
        return {
          ...base,
          type: 'input',
          placeholderStyles: INPUT_PLACEHOLDER_STYLES,
          validation,
        };
      }
      // multi_select is only ever sent by the builder (kula-ai/core #5926); a
      // field with no `kind` at all is the legacy hardcoded list, always single.
      const isMulti = field.kind === 'multi_select';
      const yesNo = !isMulti && isYesNoOptions(field.options);
      // Job Level codes carry a numeric hierarchy — order them 000 -> 010 -> alphanumerics.
      const isJobLevel =
        /job\s*level/i.test(field.label) || /job\s*level/i.test(field.id);
      const sourceOptions = isJobLevel
        ? [...field.options].sort((a, b) => {
            const ca = typeof a === 'string' ? a : a.code;
            const cb = typeof b === 'string' ? b : b.code;
            return ca.localeCompare(cb, undefined, { numeric: true });
          })
        : field.options;
      // Show name + code on dropdowns (not Yes/No radios); skip if name already has it.
      const options = sourceOptions.map((opt) => {
        if (typeof opt === 'string') return { label: opt, value: opt };
        const hasCode = typeof opt.code === 'string' && opt.code !== '';
        const withCode = hasCode && !yesNo && !opt.name.includes(opt.code);
        return {
          label: withCode ? `${opt.name} (${opt.code})` : opt.name,
          value: hasCode ? opt.code : `${EMPTY_CODE_PREFIX}${opt.name}`,
        };
      });
      // Yup.mixed().required() passes on `[]` (truthy but empty) — a multi-select
      // needs its own required check so an untouched field still blocks submit.
      const validation = isMulti
        ? field.required
          ? Yup.array().min(1, `${field.label} is required`)
          : Yup.array()
        : field.required
          ? Yup.mixed().required(`${field.label} is required`)
          : Yup.mixed();
      if (yesNo) {
        return {
          ...base,
          type: 'radio',
          options,
          radioOptionsDirection: 'row',
          validation,
        };
      }
      return {
        ...base,
        type: 'select',
        options,
        isMulti,
        valueKey: 'value',
        labelKey: 'label',
        isClearable: true,
        validation,
      };
    }
    default:
      return null;
  }
};

// A field still on the legacy hardcoded list sends its prefilled date as ISO
// regardless of the tenant's format setting; a field from the builder sends it
// in that tenant format (field.format) instead — try ISO first, then that.
const parseDarwinboxDate = (
  raw: string,
  format?: string | null
): Date | null => {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (isoMatch) {
    // Build at local midnight to avoid TZ shifts.
    const [, y, m, d] = isoMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  const parsed = parseDate(raw, dateFnsTokenFor(format), new Date());
  return isValidDate(parsed) ? parsed : null;
};

export interface DarwinboxConvertedSchema {
  formConfig: any[];
  initialValues: Record<string, any>;
}

export const convertDarwinboxSchemaToFormConfig = (
  fields: DarwinboxFormField[] = []
): DarwinboxConvertedSchema => {
  const initialValues: Record<string, any> = {};

  const fieldConfigs = fields
    .map((field) => {
      if (field.value !== null && field.value !== undefined) {
        if (field.type === 'date') {
          const dateObj = parseDarwinboxDate(String(field.value), field.format);
          if (dateObj) initialValues[field.id] = dateObj;
        } else {
          initialValues[field.id] = field.value;
        }
      }
      return buildFieldConfig(field);
    })
    .filter(Boolean);

  const formConfig = [
    {
      id: 'darwinbox-employee',
      type: 'wrapper',
      Wrapper: CollapseSection,
      wrapperProps: sectionWrapperProps,
      fields: fieldConfigs,
    },
  ];

  return { formConfig, initialValues };
};

// All date fields on one Darwinbox form share the same tenant date-format
// setting; read it back off whichever date field the schema produced so
// EmployeeFormDrawer can hand it to normalizeDarwinboxValuesForSubmit without
// holding on to the original DarwinboxFormField[] itself.
export const getDarwinboxDateFormat = (
  formConfig: DarwinboxConvertedSchema['formConfig']
): string | undefined => {
  const dateField = formConfig
    .flatMap((section) => section?.fields || [])
    .find((field) => field?.type === 'date');
  return dateField?.rawDateFormat;
};

// Normalize prefilled Dates and sentinel codes to the wire format Darwinbox
// expects. `dateFormat` is the tenant's Darwinbox date-format setting (e.g.
// "DD-MM-YYYY", from getDarwinboxDateFormat) — only a date field the recruiter
// never touched reaches this as a raw Date; one they picked was already
// stringified by DateField's own dateFormat callback.
export const normalizeDarwinboxValuesForSubmit = (
  values: Record<string, any>,
  dateFormat?: string | null
): Record<string, any> => {
  const outputToken = dateFnsTokenFor(dateFormat);
  const result: Record<string, any> = {};
  Object.entries(values).forEach(([key, raw]) => {
    if (raw instanceof Date) {
      result[key] = formatDate(raw, outputToken);
      return;
    }
    // A multi-select's sentinel codes travel inside its value array.
    if (Array.isArray(raw)) {
      result[key] = raw.map((item) =>
        typeof item === 'string' && item.startsWith(EMPTY_CODE_PREFIX)
          ? ''
          : item
      );
      return;
    }
    // Sentinel code -> real "".
    if (typeof raw === 'string' && raw.startsWith(EMPTY_CODE_PREFIX)) {
      result[key] = '';
      return;
    }
    result[key] = raw;
  });
  return result;
};
